/**
 * This component is the one in charge of observe a HTML node so when the user is close to it, dispatch an
 * event that will trigger the funnel that loads more results.
 */
import { Observable, distinctUntilChanged, distinctUntilKeyChanged, filter, map, takeWhile, zip } from 'rxjs';
import { store, store$ } from '@redux/store';
import { 
  searchParametersActions,
  selectFetchResultListingsStatus } from 'features/searchResultsFacets';
import LoadingStatus from 'enums/loadingStatus';
import {
  resultStatusAndResultsPresentationType,
  selectPageInformationAndFetchResultsStatus } from 'pages/searchResults/redux/selectors';
import ResultPresentationType from 'layout/constants/resultPresentationType';
import { selectResultsPresentationType } from 'layout/redux/selectors';

/**
 * Basic configuration for the component. It has an intersection observer called "intersectionObserver" 
 * to observe a given node and load results when the user is close to it.
 * It also has a RXJS observable called "observableObserver" that will emit a signal when the intersection
 * observer is intersecting with the observed node, this allow us to subscribe to that event
 * and run as many callbacks as we might need.
 * Finally, it has a reference to a HTML node called "infiniteScrollTrigger" which is the DOM element to
 * be observed in order to bring more results.
 */
const configuration = {
  intersectionObserver: null,
  observableObserver: null,
  infiniteScrollTrigger: document.querySelector('[data-more-results-check-point]'),
}

/**
 * fetchNextResults: dispatches an event so the funnel of bringing more results is triggered.
 */
const fetchNextResults = () => {
  store.dispatch(searchParametersActions.fetchPagingControlsNext());
}

/**
 * createObservable: this function will create the intersection observer and the observable to start
 * checking the infinite scroll trigger in order to load more results.
 */
const createObservable = () => {
  const observable$ = new Observable((observableObserver) => {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.5,
    };
    
    const onIntersection = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          observableObserver.next(entry);
        }
      });
    };
  
    configuration.intersectionObserver = new IntersectionObserver(onIntersection, options);
    configuration.intersectionObserver.observe(configuration.infiniteScrollTrigger);
    configuration.observableObserver = observableObserver;
  });

  /**
   * This observable will emit values every time results are loaded,
   * including the initial load from the server side.
   */
  const onReadyToFetchNewResults$ = store$.pipe(
    map(selectPageInformationAndFetchResultsStatus),
    distinctUntilKeyChanged('fetchResultListingsStatus'),
    takeWhile(() => configuration.intersectionObserver),
    filter(({resultPresentationType}) =>
      resultPresentationType === ResultPresentationType.InfiniteScrolling.toString()),
    filter(({fetchResultListingsStatus}) =>
      fetchResultListingsStatus.name === LoadingStatus.Succeeded.toString() ||
      fetchResultListingsStatus.name === LoadingStatus.Idle.toString()),
    filter(({page, pageSize, listingCount}) => page * pageSize <= listingCount)
  );

  zip([observable$, onReadyToFetchNewResults$]).subscribe(fetchNextResults);
}

/**
 * destroyObservable: completes the RXJS observable and disconnects the intersection observable since
 * the Search Results Page shown is not of the type of infinite scrolling.
 * Sets these two values to null after disconnection and completeness.
 */
const destroyObservable = () => {
  configuration.intersectionObserver.disconnect();
  configuration.observableObserver.complete();
  configuration.intersectionObserver = null;
  configuration.observableObserver = null;
}

/**
 * manageObserver: this function pauses and resumes the intersection observer based on the
 * results listing status of the fetch operation. We disconnect the observer when results
 * are being loaded and connect it again when loading operation was succeeded.
 * This is particulary usefull to avoid intersecting several times in a row without waiting
 * for results to render, this could provoke to fetch page n and suddenly cancel it to fetch
 * page n+1, which is an issue since the user won't get to see all available results.
 * @param {LoadingStatus} loadingStatus of the current fetch results operation.
 * @returns 
 */
const manageObserver = (loadingStatus) => {
  if (loadingStatus.name === LoadingStatus.Loading.toString()) {
    configuration.intersectionObserver.disconnect();
    return;
  }

  configuration.intersectionObserver.observe(configuration.infiniteScrollTrigger);
}

/**
 * Subscribing to the store, if loading status is idle (initial load rendered results) or
 * succeeded (ajax rendered results), and also the current Search Results Page is showing results
 * in an infinite scroll way, then we start the observables to be ready to load more results.
 */
store$.pipe(
  map(resultStatusAndResultsPresentationType),
  distinctUntilKeyChanged('fetchResultListingStatus'),
  filter(({fetchResultListingStatus}) =>
    fetchResultListingStatus.name === LoadingStatus.Succeeded.toString() ||
    fetchResultListingStatus.name === LoadingStatus.Idle.toString()),
  filter(({presentationType}) => presentationType === ResultPresentationType.InfiniteScrolling.toString()),
  filter(() => !configuration.intersectionObserver),
  ).subscribe(createObservable);

/**
 * Subscribing to the store, if the current Search Results Page is showing results in a way 
 * distinct than infinite scroll, then we stop the observables since they don't need to be in
 * charge of load results anymore.
 */
store$.pipe(
  map(selectResultsPresentationType),
  distinctUntilChanged(),
  filter((resultPresentationType) => resultPresentationType !== ResultPresentationType.InfiniteScrolling.toString()),
  filter(() => configuration.observableObserver),
  filter(() => configuration.intersectionObserver),
  ).subscribe(destroyObservable);

  /**
   * Subscribing to the store to detect changes in the result listing status
   */
store$.pipe(
  map(selectFetchResultListingsStatus),
  distinctUntilChanged(),
  filter(() => configuration.intersectionObserver)
).subscribe(manageObserver)